export const FORMATIONS_BY_FORMAT = {
  '4x4':  ['1-2'],
  '5x5':  ['2-2'],
  '6x6':  ['2-1-2', '2-2-1', '3-1-1'],
  '7x7':  ['3-1-2', '2-2-2', '3-2-1'],
  '8x8':  ['3-3-1', '3-2-2', '2-4-1', '2-3-2', '2-2-3', '1-3-3'],
  '9x9':  ['4-2-2', '4-3-1', '4-1-3', '3-3-2', '3-4-1'],
  '11x11': ['4-4-2', '4-3-3', '5-3-2'],
};

export const MATCH_FORMATS = Object.keys(FORMATIONS_BY_FORMAT);

export const POSITION_LABELS = { 1: 'posFW', 2: 'posMF', 3: 'posDF', 4: 'posGK', 5: 'posUNI' };

export const generateFieldPositions = (formationStr) => {
  const lines = String(formationStr || '').split('-').map(Number);
  const positions = [{ top: 90, left: 50 }]; // GK
  // Линии стоят с равным шагом от вратаря, иначе кружки смыкаются по вертикали.
  const yMap = { 1: [45], 2: [64, 30], 3: [66, 42, 18] };
  const yPositions = yMap[lines.length] || yMap[2];
  lines.forEach((count, i) => {
    const y = yPositions[i];
    for (let j = 0; j < count; j += 1) {
      positions.push({ top: y, left: (j + 1) * 100 / (count + 1) });
    }
  });
  return positions;
};

// В плотных линиях кружок должен быть меньше шага между позициями.
export const fieldDensityClass = (formationStr) => {
  const lines = String(formationStr || '').split('-').map(Number).filter(Boolean);
  const perLine = lines.length ? Math.max(...lines) : 0;
  if (perLine >= 5) return 'olm-field-dense-5';
  if (perLine === 4) return 'olm-field-dense-4';
  return '';
};

// Слоты на сервере называются gk / def_N / mid_N / fwd_N, у нас — индексы, где 0 это вратарь.
const LINE_NAMES = { 1: ['fwd'], 2: ['def', 'fwd'], 3: ['def', 'mid', 'fwd'] };

export const formationSlotKeys = (formation) => {
  const lines = String(formation || '').split('-').map(Number).filter(Boolean);
  const names = LINE_NAMES[lines.length] || LINE_NAMES[3];
  const keys = ['gk'];
  lines.forEach((count, lineIndex) => {
    for (let i = 0; i < count; i += 1) keys.push(`${names[lineIndex]}_${i}`);
  });
  return keys;
};

export const lineupToAssignments = (formation, lineup) => {
  const assignments = {};
  formationSlotKeys(formation).forEach((key, index) => {
    const playerId = Number(lineup?.[index]);
    if (playerId) assignments[key] = playerId;
  });
  return assignments;
};

const slotAlias = (slot) => String(slot ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const playerIdFrom = (value) => {
  if (value && typeof value === 'object') return Number(value.player_id ?? value.id ?? value.player?.id);
  return Number(value);
};

export const assignmentsToLineup = (formation, assignments) => {
  const lineup = {};
  if (!assignments) return lineup;
  // Слот может прийти ключом объекта или полем строки, значение — id игрока или объект игрока.
  const bySlot = new Map();
  if (Array.isArray(assignments)) {
    assignments.forEach((item) => {
      const slot = item?.slot ?? item?.lineup_position_id ?? item?.position ?? item?.lineup_position;
      bySlot.set(slotAlias(slot), playerIdFrom(item?.player_id ?? item?.id ?? item));
    });
  } else {
    Object.entries(assignments).forEach(([slot, value]) => {
      // Карта может быть и обратной: id игрока → слот.
      if (Number(slot) && typeof value === 'string' && !Number(value)) bySlot.set(slotAlias(value), Number(slot));
      else bySlot.set(slotAlias(slot), playerIdFrom(value));
    });
  }
  formationSlotKeys(formation).forEach((key, index) => {
    const playerId = bySlot.get(slotAlias(key));
    if (playerId) lineup[index] = playerId;
  });
  return lineup;
};

// Ответ рута — объект с ключами-строками по id команды.
export const teamLineup = (lineups, teamId) => {
  if (!lineups || teamId == null) return null;
  if (Array.isArray(lineups)) {
    return lineups.find((item) => Number(item?.team_id) === Number(teamId)) || null;
  }
  return lineups[String(teamId)] || lineups[Number(teamId)] || null;
};

export const emptyLineupSetup = () => ({
  selectedPlayers: {},
  matchFormat: '',
  homeFormation: '',
  guestFormation: '',
  homeLineup: {},
  guestLineup: {},
  homeColor: '#3498db',
  guestColor: '#e74c3c',
});

export const setupFromRoster = (matchApplication = []) => {
  const setup = emptyLineupSetup();
  matchApplication.forEach((player) => { setup.selectedPlayers[player.id] = Boolean(player.active); });
  return setup;
};

export const setupFromServer = (lineups, homeTeamId, guestTeamId) => {
  const home = teamLineup(lineups, homeTeamId);
  const guest = teamLineup(lineups, guestTeamId);
  if (!home && !guest) return null;
  const format = (row) => row?.match_format || row?.format || '';
  const slots = (row) => assignmentsToLineup(
    row?.formation,
    Array.isArray(row) ? row : (row?.assignments || row?.players || row?.lineup)
  );
  const fromCaptain = (row) => row?.from_captain === true || row?.from_captain === 1 || row?.from_captain === '1';
  return {
    matchFormat: format(home) || format(guest),
    homeFormation: home?.formation || '',
    guestFormation: guest?.formation || '',
    homeLineup: slots(home),
    guestLineup: slots(guest),
    homeFromCaptain: fromCaptain(home),
    guestFromCaptain: fromCaptain(guest),
  };
};

export const isLineupSetupComplete = (setup) => Boolean(
  setup
  && setup.matchFormat
  && setup.homeFormation
  && setup.guestFormation
  && Object.keys(setup.homeLineup || {}).length
  && Object.keys(setup.guestLineup || {}).length
);

// Расстановка живёт только на клиенте: сервер хранит заявку и вратаря, но не схему и слоты.
export const SETUP_SESSION_KEY = 'olm_match_setup';

export const loadLineupSetup = (matchId) => {
  try {
    const raw = sessionStorage.getItem(SETUP_SESSION_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    if (!saved) return null;
    if (matchId != null && Number(saved.matchId) !== Number(matchId)) return null;
    return saved;
  } catch (error) {
    return null;
  }
};

export const saveLineupSetup = (data) => {
  try {
    sessionStorage.setItem(SETUP_SESSION_KEY, JSON.stringify(data));
  } catch (error) { /* ignore */ }
};

export const clearLineupSetup = () => {
  try {
    sessionStorage.removeItem(SETUP_SESSION_KEY);
  } catch (error) { /* ignore */ }
};
